import type { PropsWithChildren } from 'react';
import { createContext, useContext, useEffect, useState } from 'react';
import { useSettings } from '@wu/widget-core';
import { useCalendarMetaQuery } from '../common/use-calendar-meta.query';

interface CurrentTimeZoneContextProps {
  timeZone: string | undefined;
}

const CurrentTimeZoneContext = createContext<
  CurrentTimeZoneContextProps | undefined
>(undefined);

export const useCurrentTimeZone = (): CurrentTimeZoneContextProps => {
  const context = useContext(CurrentTimeZoneContext);
  if (!context) {
    throw new Error(
      'useCurrentTimeZone must be used within an CurrentTimeZoneProvider'
    );
  }
  return context;
};

export const CurrentTimeZoneProvider = ({ children }: PropsWithChildren) => {
  const {
    timeZone,
    integrationGoogleCalendarAuth,
    integrationGoogleCalendarCalendar,
    widgetToken
  } = useSettings();
  const { data, isFetched } = useCalendarMetaQuery({
    integrationGoogleCalendarAuth,
    integrationGoogleCalendarCalendar,
    widgetToken
  });

  const [currentTimeZone, setCurrentTimeZone] = useState(timeZone);

  useEffect(() => {
    if (data?.timeZone) {
      setCurrentTimeZone(data.timeZone);
    } else {
      setCurrentTimeZone(timeZone);
    }
  }, [timeZone, data, isFetched]);

  return (
    <CurrentTimeZoneContext.Provider value={{ timeZone: currentTimeZone }}>
      {children}
    </CurrentTimeZoneContext.Provider>
  );
};
